import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, MapPin, Clock, CheckCircle } from 'lucide-react';
import { CustomerService } from '../../services/customerService';

const Track = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [phone, setPhone] = useState(user?.phone || '');
  const [awb, setAwb] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');

  const search = async () => {
    if (!phone && !awb) {
      setError('Please enter phone number or AWB code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (awb) {
        const result = await CustomerService.trackShipmentByAWB(awb);
        if (result.success) {
          setOrders([result.data]);
        } else {
          setError(result.error || 'No orders found');
        }
      } else if (phone) {
        const result = await CustomerService.getActiveOrders(user?.id || phone);
        if (result.success) {
          setOrders(result.data || []);
        } else {
          setError(result.error || 'No orders found');
        }
      }
    } catch (err) {
      setError('Failed to search orders. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Track Your Order</h1>
        <p className="text-gray-600 mt-1">Enter your phone number or AWB code to track</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">AWB Code (Optional)</label>
            <input
              type="text"
              placeholder="Enter AWB code"
              value={awb}
              onChange={(e) => setAwb(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          <button
            onClick={search}
            disabled={loading}
            className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            <Search size={20} />
            <span>{loading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>
      </div>

      {orders.length > 0 && (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.order_id || order.id}
              onClick={() => navigate(`/order/${order.order_id || order.id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {order.customer_name || 'Order'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Order ID: {order.order_id || order.id}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {order.shipping_status === 'delivered' ? (
                    <CheckCircle size={24} className="text-green-600" />
                  ) : (
                    <Package size={24} className="text-blue-600" />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Status: <span className="font-medium">{order.current_status_type || order.shipping_status || 'N/A'}</span>
                  </span>
                </div>
                {order.awb_code && (
                  <div className="flex items-center space-x-2">
                    <Package size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      AWB: <span className="font-medium">{order.awb_code}</span>
                    </span>
                  </div>
                )}
                {order.address && (
                  <div className="flex items-center space-x-2">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">{order.address}</span>
                  </div>
                )}
                {order.order_created_at && (
                  <div className="flex items-center space-x-2">
                    <Clock size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Ordered: {new Date(order.order_created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Track;

